import PropTypes from "prop-types";
import React, { Component } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  InputLabel,
  FormControl
} from "@material-ui/core";
import { withRouter } from "react-router-dom";
import styled from "styled-components";
import withTheme from "@material-ui/core/styles/withTheme";
import HeaderInfo from "../../waybee-ui/HeaderInfo/HeaderInfo";
import Group from "../../waybee-ui/Group";
import Divider from "../../waybee-ui/Divider";
import CollapseGroup from "../../waybee-ui/CollapseGroup";
import CollapseSubGroup from "../../waybee-ui/CollapseSubGroup";
import BackButton from "../../components/BackButton";
import StudentService from "../../services/StudentService";
import PeriodPerformanceChart from "./components/PeriodPerformanceChart";
import ChartService from "../../services/ChartService";
import OverallPerformanceChart from "./components/OverallPerformanceChart";
import GradeDistributionChart from "./components/GradeDistributionChart";

const HeaderGrid = styled(Grid)`
  margin-bottom: -20px;
`;

const HeaderGroup = styled(Group)`
  padding-bottom: 0;
`;

const StageText = styled.span`
  font-size: 14px;
  color: ${props => props.color};
  font-weight: bold;
`;

const StageSelect = styled(Select)`
  margin: 8px 16px;
`;

class StudentCharts extends Component {
  constructor(props) {
    super(props);
    this.state = {
      student: null,
      stages: [],
      selectedStage: null,
      periodPerformanceData: null,
      overallPerformanceData: null,
      gradeDistributionData: null,
      gradeDistributionDataSelected: null,
      disciplines: [],
      types: [
        {
          id: "course",
          name: "Curso"
        },
        {
          id: "classe",
          name: "Turma"
        }
      ],
      distFilter: {
        type: "classe"
      }
    };
  }

  async componentDidMount() {
    const { match } = this.props;
    const { studentId } = match.params;
    this.getStudent(studentId);
  }

  getStudent = studentId => {
    StudentService.getStudentById(studentId)
      .then(student => {
        this.setState({ student });
        this.getChartStages(student.waybeeUserId);
      })
      .catch(e => {
        console.error(e);
      });
  };

  getChartStages = studentId => {
    ChartService.getChartStages(studentId)
      .then(stages => {
        this.setState(
          { stages, selectedStage: stages[0].id },
          this.getGraphData
        );
      })
      .catch(e => {
        console.error(e);
      });
  };

  onChangeStage = async e => {
    const { student } = this.state;
    this.setState({ selectedStage: e.target.value }, async () => {
      await this.getOverallPerformanceChart(student.waybeeUserId);
    });
  };

  getPeriodPerformanceChart = studentId => {
    ChartService.getPeriodPerformance(studentId)
      .then(periodPerformanceData => {
        this.setState({ periodPerformanceData });
      })
      .catch(e => {
        console.error(e);
      });
  };

  getOverallPerformanceChart = studentId => {
    const { selectedStage } = this.state;

    ChartService.getOverallPerformanceChart(studentId, selectedStage)
      .then(overallPerformanceData => {
        this.setState({ overallPerformanceData });
      })
      .catch(e => {
        console.error(e);
      });
  };

  getGradeDistributionChart = studentId => {
    const { selectedStage, distFilter } = this.state;
    const { theme } = this.props;

    ChartService.getGradeDistribuitionChart(
      studentId,
      selectedStage,
      distFilter.type
    )
      .then(res => {
        const gradeDistributionData = res;

        if (gradeDistributionData) {
          gradeDistributionData.datasets = gradeDistributionData.datasets.map(
            dataset => {
              return {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
                ...dataset
              };
            }
          );
        }
        this.setState(
          {
            gradeDistributionData,
            disciplines: gradeDistributionData.disciplines
          },
          this.setupDisciplines
        );
      })
      .catch(e => {
        console.error(e);
      });
  };

  getGraphData = async () => {
    const { student } = this.state;
    this.getOverallPerformanceChart(student.waybeeUserId);
    this.getPeriodPerformanceChart(student.waybeeUserId);
    this.getGradeDistributionChart(student.waybeeUserId);
  };

  onChangeType = value => {
    const { distFilter, student } = this.state;
    this.setState({ distFilter: { ...distFilter, type: value } }, () =>
      this.getGradeDistributionChart(student.waybeeUserId)
    );
  };

  setupDisciplines = () => {
    const { disciplines, distFilter, gradeDistributionData } = this.state;

    const gradeDistributionDataSelected = {
      labels: gradeDistributionData.labels,
      datasets: [
        gradeDistributionData.datasets.find(
          dataset => dataset.discipline.id === disciplines[0].id
        )
      ]
    };

    this.setState({
      distFilter: {
        ...distFilter,
        discipline: disciplines[0].id
      },
      gradeDistributionDataSelected
    });
  };

  onChangeDiscipline = val => {
    const { distFilter, gradeDistributionData } = this.state;

    const gradeDistributionDataSelected = {
      labels: gradeDistributionData.labels,
      datasets: [
        gradeDistributionData.datasets.find(
          dataset => dataset.discipline.id === val
        )
      ]
    };

    this.setState({
      distFilter: { ...distFilter, discipline: val },
      gradeDistributionDataSelected
    });
  };

  render() {
    const {
      student,
      stages,
      selectedStage,
      periodPerformanceData,
      overallPerformanceData,
      gradeDistributionDataSelected,
      distFilter,
      types,
      disciplines
    } = this.state;

    const { theme } = this.props;

    if (!student) {
      return (
        <Box p={6} textAlign="center">
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        <BackButton to={`/student/${student.id}`} />
        <HeaderGrid item xs={12}>
          <HeaderGroup>
            <Box mb={2}>
              <HeaderInfo
                name={student.name}
                info={`R.A: ${student.ra}`}
                photo={student.photo}
              />
            </Box>
            <Divider />
          </HeaderGroup>
        </HeaderGrid>
        <Grid item container xs={12}>
          <Grid item xs={12}>
            <CollapseGroup title="Gráficos">
              {periodPerformanceData && (
                <CollapseSubGroup title="Desempenho por periodo">
                  <PeriodPerformanceChart data={periodPerformanceData} />
                </CollapseSubGroup>
              )}
              {overallPerformanceData && (
                <CollapseSubGroup title="Desempenho Geral">
                  <Grid item container xs={12}>
                    <Grid item xs={3}>
                      <StageSelect
                        value={selectedStage}
                        onChange={this.onChangeStage}
                        renderValue={selected => {
                          const actualStage = stages.find(
                            stage => stage.id === selected
                          );
                          if (!actualStage) return;
                          // eslint-disable-next-line consistent-return
                          return (
                            <StageText color={theme.palette.primary.main}>
                              {actualStage.name}
                            </StageText>
                          );
                        }}
                        fullWidth
                      >
                        {stages.map(stage => {
                          return (
                            <MenuItem key={stage.id} value={stage.id}>
                              <StageText color={theme.palette.default.main}>
                                {stage.name}
                              </StageText>
                            </MenuItem>
                          );
                        })}
                      </StageSelect>
                    </Grid>
                  </Grid>
                  <OverallPerformanceChart data={overallPerformanceData} />
                </CollapseSubGroup>
              )}
              {gradeDistributionDataSelected && (
                <CollapseSubGroup title="Gráfico de distribuição de notas">
                  <Grid item container xs={12}>
                    <Grid item xs={2}>
                      <FormControl fullwidth>
                        <InputLabel style={{ marginLeft: 16, width: "114px" }}>
                          Turma ou Curso
                        </InputLabel>
                        <StageSelect
                          value={distFilter.type}
                          onChange={e => this.onChangeType(e.target.value)}
                        >
                          {types.map(type => (
                            <MenuItem value={type.id}>{type.name}</MenuItem>
                          ))}
                        </StageSelect>
                      </FormControl>
                    </Grid>
                    <Grid item xs={2}>
                      <FormControl fullwidth>
                        <InputLabel>Disciplina</InputLabel>
                        <Select
                          value={distFilter.discipline}
                          onChange={e =>
                            this.onChangeDiscipline(e.target.value)}
                        >
                          {disciplines.map(discipline => (
                            <MenuItem value={discipline.id}>
                              {discipline.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                  <GradeDistributionChart
                    data={gradeDistributionDataSelected}
                  />
                </CollapseSubGroup>
              )}
            </CollapseGroup>
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

StudentCharts.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.array
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  theme: PropTypes.object.isRequired // Injected by withTheme()
};

export default withTheme(withRouter(StudentCharts));
